import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { Toaster } from "./components/ui/sonner";
import NutritionChatWidget from "./components/NutritionChatWidget";
import OnboardingTour from "./components/OnboardingTour";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Home from "./pages/Home";
import Menu from "./pages/Menu";
import Cart from "./pages/Cart";
import Subscriptions from "./pages/Subscriptions";
import Dashboard from "./pages/Dashboard";
import AdminPanel from "./pages/AdminPanel";
import AdminDashboard from "./pages/AdminDashboard";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import AuthCallback from "./pages/AuthCallback";
import CompleteProfile from "./pages/CompleteProfile";
import UserSettings from "./pages/UserSettings";
import BillingSettings from "./pages/BillingSettings";
import PaymentMethodsSettings from "./pages/PaymentMethodsSettings";
import NutritionSettings from "./pages/NutritionSettings";
import RecurringOrdersSettings from "./pages/RecurringOrdersSettings";
import OperationsDashboard from "./pages/OperationsDashboard";
import ProductDetail from "./pages/ProductDetail";
import FAQPage from "./pages/FAQPage";
import LoginPage from "./pages/LoginPage";
import ComprasDashboard from "./pages/ComprasDashboard";
import ProduccionDashboard from "./pages/ProduccionDashboard";
import ProveedorDashboard from "./pages/ProveedorDashboard";
import EmpresaDashboard from "./pages/EmpresaDashboard";
import ClienteDashboard from "./pages/ClienteDashboard";
import DeckPage from "./pages/DeckPage";
import MenuCalendar from "./pages/MenuCalendar";
import QRManager from "./pages/QRManager";

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id before rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/menu" element={<Menu />} />
      <Route path="/product/:productId" element={<ProductDetail />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/subscriptions" element={<Subscriptions />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'master_admin']}><AdminPanel /></ProtectedRoute>} />
      <Route path="/admin-dashboard" element={<ProtectedRoute allowedRoles={['admin', 'master_admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/complete-profile" element={<CompleteProfile />} />
      <Route path="/settings" element={<UserSettings />} />
      <Route path="/settings/billing" element={<BillingSettings />} />
      <Route path="/settings/payment-methods" element={<PaymentMethodsSettings />} />
      <Route path="/settings/nutrition" element={<NutritionSettings />} />
      <Route path="/settings/recurring-orders" element={<RecurringOrdersSettings />} />
      <Route path="/operations" element={<ProtectedRoute allowedRoles={['admin', 'master_admin', 'compras', 'produccion']}><OperationsDashboard /></ProtectedRoute>} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/compras" element={<ProtectedRoute allowedRoles={['admin', 'master_admin', 'compras']}><ComprasDashboard /></ProtectedRoute>} />
      <Route path="/produccion" element={<ProtectedRoute allowedRoles={['admin', 'master_admin', 'produccion']}><ProduccionDashboard /></ProtectedRoute>} />
      <Route path="/proveedor" element={<ProtectedRoute allowedRoles={['admin', 'master_admin', 'proveedor']}><ProveedorDashboard /></ProtectedRoute>} />
      <Route path="/empresa" element={<ProtectedRoute allowedRoles={['admin', 'master_admin', 'cliente_empresa']}><EmpresaDashboard /></ProtectedRoute>} />
      <Route path="/cliente" element={<ProtectedRoute allowedRoles={['admin', 'master_admin', 'cliente_comprador']}><ClienteDashboard /></ProtectedRoute>} />
      <Route path="/deck" element={<DeckPage />} />
      <Route path="/menu-calendar" element={<MenuCalendar />} />
      <Route path="/qr-manager" element={<ProtectedRoute allowedRoles={['admin', 'master_admin']}><QRManager /></ProtectedRoute>} />
    </Routes>
  );
}

function AppContent() {
  const { user, isProfileComplete } = useAuth();
  
  return (
    <>
      <AppRouter />
      <NutritionChatWidget />
      <OnboardingTour user={user} isProfileComplete={isProfileComplete} />
      <Toaster position="top-right" richColors />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
